// index.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import 'antd/dist/antd.css';
import './styles/index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as echarts from 'echarts';

/* ================================================================
 *  ★ 第一层防御：patch echarts 内部（tooltip + ZRender Handler）
 * ================================================================ */
patchEchartsTooltip();
patchZRenderHandler();

/* ================================================================
 *  ★ 第二层防御：全局错误捕获，静默 echarts-gl 已知错误防红屏
 * ================================================================ */
installGlobalEchartsErrorSilencer();

// ---------- patch 函数实现 ----------

function patchEchartsTooltip(): void {
  try {
    const tempDiv = document.createElement('div');
    tempDiv.style.cssText =
      'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';
    document.body.appendChild(tempDiv);

    const tempChart = echarts.init(tempDiv);
    tempChart.setOption({ tooltip: { show: false }, series: [] });

    const views: any[] = (tempChart as any)._componentsViews || [];
    let tooltipViewProto: any = null;

    for (const view of views) {
      if (
        view &&
        view.__proto__ &&
        typeof view.__proto__._showSeriesItemTooltip === 'function'
      ) {
        tooltipViewProto = view.__proto__;
        break;
      }
    }

    tempChart.dispose();
    document.body.removeChild(tempDiv);

    if (!tooltipViewProto) {
      console.warn('[ECharts Patch] 未找到 TooltipView 原型，跳过 patch');
      return;
    }

    const original = tooltipViewProto._showSeriesItemTooltip;
    tooltipViewProto._showSeriesItemTooltip = function patchedShowSeriesItemTooltip(
      ...args: any[]
    ) {
      try {
        return original.apply(this, args);
      } catch (err: any) {
        if (isKnownEchartsError(err)) return;
        throw err;
      }
    };

    console.info('[ECharts Patch] tooltip _showSeriesItemTooltip 已 patch');
  } catch (e) {
    console.warn('[ECharts Patch] tooltip patch 失败，忽略:', e);
  }
}

/**
 * ★ 核心新增：Patch ZRender 的 Handler.mousemove
 *
 * 报错堆栈：
 *   Handler.mousemove → 读取 null 的 'target'
 *
 * 原因：geo3D 地图切换时，ZRender Handler 内部的 _hoveredTarget
 * 或事件代理对象被置空，但 mousemove 回调仍在执行。
 *
 * 方案：从 echarts 实例拿到 ZRender 的 Handler 原型，
 *       用 try-catch 包裹 mousemove 方法。
 */
function patchZRenderHandler(): void {
  try {
    // 创建临时实例获取 ZRender Handler 原型
    const tempDiv = document.createElement('div');
    tempDiv.style.cssText =
      'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';
    document.body.appendChild(tempDiv);

    const tempChart = echarts.init(tempDiv);

    // echarts 内部：chart._zr 是 ZRender 实例
    // zr.handler 是 Handler 实例
    const zr = (tempChart as any)._zr || (tempChart as any).getZr?.();
    const handler = zr?.handler;

    tempChart.dispose();
    document.body.removeChild(tempDiv);

    if (!handler || !handler.__proto__) {
      console.warn('[ZRender Patch] 未找到 Handler 原型，跳过 patch');
      return;
    }

    const handlerProto = handler.__proto__;

    // ★ patch mousemove — 这是报错的入口方法
    if (typeof handlerProto.mousemove === 'function') {
      const origMousemove = handlerProto.mousemove;
      handlerProto.mousemove = function patchedMousemove(...args: any[]) {
        try {
          return origMousemove.apply(this, args);
        } catch (err: any) {
          if (isKnownEchartsError(err)) {
            // 静默：仅在开发环境打 debug 日志
            if (process.env.NODE_ENV === 'development') {
              console.debug('[ZRender Patch] mousemove 忽略已知错误:', err.message);
            }
            return;
          }
          throw err;
        }
      };
    }

    // ★ 同时 patch mouseout / mouseup，同类竞态也可能在这些方法中出现
    for (const method of ['mouseout', 'mouseup', 'touchmove', 'touchend'] as const) {
      if (typeof handlerProto[method] === 'function') {
        const orig = handlerProto[method];
        handlerProto[method] = function patched(...args: any[]) {
          try {
            return orig.apply(this, args);
          } catch (err: any) {
            if (isKnownEchartsError(err)) return;
            throw err;
          }
        };
      }
    }

    console.info('[ZRender Patch] Handler mousemove/mouseout/mouseup/touch 已 patch');
  } catch (e) {
    console.warn('[ZRender Patch] patch 失败，忽略:', e);
  }
}

/**
 * ★ 判断是否是 echarts / echarts-gl / zrender 的已知竞态错误
 * 集中管理，便于后续添加新的错误模式
 */
function isKnownEchartsError(err: any): boolean {
  const msg = err?.message || '';
  // 错误模式列表 — 持续补充
  const knownPatterns = [
    "reading 'target'",           // ★ 本次报错
    "reading 'getData'",
    "reading 'get'",
    "'getData' of undefined",
    "'get' of undefined",
    "'getData' of null",
    "'get' of null",
    "'target' of null",
    "reading 'refreshImmediately'",
    "'refreshImmediately' of null",
    "reading 'bindGetters'",
    "reading 'bindBuilders'",
    // echarts-gl 内部常见
    "reading 'bindings'",
    "reading 'bindTexture'",
    "reading 'bindBuffer'",
    "reading 'addRoot'",
    "reading 'getRoots'",
  ];
  return knownPatterns.some((p) => msg.includes(p));
}

/**
 * ★ 第二层防御：全局 error / unhandledrejection 事件监听
 *
 * 即使 patch 遗漏了某些路径，这一层也能阻止红屏。
 * React 的 overlay 是由 react-error-overlay（CRA dev server）
 * 通过监听 window 'error' 事件触发的，我们在它之前拦截。
 */
function installGlobalEchartsErrorSilencer(): void {
  // ★ 拦截同步错误
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      if (isEchartsRelatedError(event)) {
        // 阻止事件传播 → CRA 的 react-error-overlay 不会收到 → 不红屏
        event.stopImmediatePropagation();
        event.preventDefault();
        console.warn('[Global Silencer] 已拦截 echarts 相关错误:', event.message);
        return false;
      }
    },
    true, // ★ 捕获阶段，比 CRA overlay 更早执行
  );

  // ★ 拦截异步错误（Promise rejection）
  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      const msg = event.reason?.message || String(event.reason || '');
      if (isKnownEchartsError({ message: msg })) {
        event.stopImmediatePropagation();
        event.preventDefault();
        console.warn('[Global Silencer] 已拦截 echarts promise rejection:', msg);
      }
    },
    true,
  );
}

/**
 * 判断全局 ErrorEvent 是否来自 echarts 相关代码
 */
function isEchartsRelatedError(event: ErrorEvent): boolean {
  // 1. 先检查错误消息是否匹配已知模式
  if (isKnownEchartsError({ message: event.message })) {
    return true;
  }

  // 2. 检查错误对象本身
  if (event.error && isKnownEchartsError(event.error)) {
    return true;
  }

  // 3. 检查堆栈中是否包含 echarts/zrender 关键字
  const stack = event.error?.stack || '';
  if (
    stack.includes('echarts') ||
    stack.includes('zrender') ||
    stack.includes('echarts-gl') ||
    stack.includes('Handler.mousemove') ||
    stack.includes('HandlerDomProxy') ||
    stack.includes('Bindable.bindGetters') ||
    stack.includes('bindBuilders')
  ) {
    return true;
  }

  return false;
}

/* ================================================================
 *  ★ 正常的 React 渲染
 * ================================================================ */

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

reportWebVitals();
